import { useState } from "react";
import { Info } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { formatValueByUnit } from "../../lib/coachUtils";
import { normalizeScoreColumns, scoreLabelOf } from "../../lib/performanceModelUtils";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";

const TIME_UNITS = new Set(["seconds", "minutes", "minutes_seconds"]);

const refsOf = (exercise) =>
	(Array.isArray(exercise?.scoring_references) ? exercise.scoring_references : []).filter(
		(ref) => Number.isFinite(Number(ref?.score)) && Number.isFinite(Number(ref?.value)),
	);

/** Il livello raggiunto, per evidenziare la sua soglia nella tabella. */
const reachedColumn = (exercise, columns) => {
	const score = Number(exercise?.score_value);
	if (exercise?.score_value == null || !Number.isFinite(score)) return null;
	const reached = columns.filter((column) => column <= score + 1e-9);
	return reached.length ? reached[reached.length - 1] : null;
};

/**
 * «Come si leggono i livelli» di un dominio del profilo prestativo.
 *
 * Prende il posto della descrizione scritta di fianco al nome del dominio,
 * troncata e illeggibile: un link apre una finestra con il funzionamento del
 * livello, la tabella usata (categoria di peso o genere), la scala coi suoi
 * nomi, le soglie di ogni esercizio con quella raggiunta e, in fondo, la
 * descrizione del dominio, che chi scrive il modello usa per dire da dove
 * vengono i numeri. Tutto dai dati della risposta: vale per ogni sport.
 *
 * Lo usano il profilo del coach (`PerformanceProfilePreview`) e quello
 * dell'atleta (`pages/PerformanceProfile.js`).
 *
 * Props: domain (un dominio della risposta del profilo, con `exercises`).
 */
export function LevelsGuideButton({ domain }) {
	const { t } = useLanguage();
	const [open, setOpen] = useState(false);
	const exercises = (domain?.exercises || []).filter((exercise) => refsOf(exercise).length >= 2);
	const description = String(domain?.description || "").trim();
	if (!exercises.length && !description) return null;

	const columns = normalizeScoreColumns(domain?.scoring_columns);
	const labels = domain?.scoring_labels || {};
	const classLabel =
		exercises.find((exercise) => exercise.scoring_basis === "weight_class")?.weight_class?.label ||
		null;
	const usesFallback = exercises.some((exercise) =>
		["gender", "other_gender", "generic"].includes(exercise.scoring_basis),
	);
	const hasTimes = exercises.some((exercise) => TIME_UNITS.has(exercise.unit));
	const hasLabels = columns.some((score) => scoreLabelOf(labels, score));

	return (
		<>
			<button
				type="button"
				onClick={() => setOpen(true)}
				className="inline-flex items-center gap-1 shrink-0 text-[11px] text-volt hover:text-volt/80 underline-offset-2 hover:underline"
			>
				<Info className="w-3.5 h-3.5" />
				{t("lgOpen")}
			</button>
			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="bg-card border-border max-w-4xl">
					<DialogHeader>
						<DialogTitle className="font-heading pr-6">
							{t("lgTitle").replace("{domain}", domain?.name || "")}
						</DialogTitle>
						<DialogDescription className="text-xs">{t("lgIntro")}</DialogDescription>
					</DialogHeader>
					<div className="space-y-5 text-sm">
						{exercises.length > 0 && (
							<section className="space-y-1.5">
								<p className="label-tactical">{t("lgHowTitle")}</p>
								<p className="text-muted-foreground leading-relaxed">{t("lgHowScore")}</p>
								{hasTimes && (
									<p className="text-muted-foreground leading-relaxed">{t("lgHowTimes")}</p>
								)}
								{classLabel && (
									<p className="text-muted-foreground leading-relaxed">
										{t("lgWeightClass").replace("{class}", classLabel)}
									</p>
								)}
								{usesFallback && (
									<p className="text-muted-foreground leading-relaxed">{t("lgFallback")}</p>
								)}
							</section>
						)}

						<section className="space-y-2">
							<p className="label-tactical">{t("lgScaleTitle")}</p>
							{hasLabels ? (
								<ol className="grid grid-cols-1 gap-1 sm:grid-cols-2">
									{[...columns].reverse().map((score) => (
										<li key={score} className="flex items-baseline gap-2">
											<span className="w-8 shrink-0 text-right font-heading text-volt tabular-nums">
												{score}
											</span>
											<span>{scoreLabelOf(labels, score) || "—"}</span>
										</li>
									))}
								</ol>
							) : (
								<p className="text-muted-foreground">
									{t("lgScaleNumbers")
										.replace("{min}", String(columns[0]))
										.replace("{max}", String(columns[columns.length - 1]))}
								</p>
							)}
						</section>

						{exercises.length > 0 && (
							<section className="space-y-2">
								<p className="label-tactical">
									{classLabel
										? t("lgThresholdsClass").replace("{class}", classLabel)
										: t("lgThresholds")}
								</p>
								<p className="text-[11px] text-muted-foreground">{t("lgThresholdsHint")}</p>
								<div className="overflow-x-auto border border-border rounded-sm">
									<table className="w-full min-w-[560px] text-xs">
										<thead>
											<tr className="bg-secondary/60 border-b border-border">
												<th className="sticky left-0 z-10 bg-secondary px-2 py-1.5 text-left font-medium">
													{t("pmColExercise")}
												</th>
												{columns.map((score) => (
													<th
														key={score}
														className="px-1 py-1.5 text-center font-medium text-volt tabular-nums"
													>
														{score}
													</th>
												))}
											</tr>
										</thead>
										<tbody>
											{exercises.map((exercise) => {
												const byScore = new Map(
													refsOf(exercise).map((ref) => [Number(ref.score), Number(ref.value)]),
												);
												const reached = reachedColumn(exercise, columns);
												return (
													<tr
														key={exercise.config_id || exercise.exercise_id}
														className="border-b border-border/60 last:border-b-0"
													>
														<td className="sticky left-0 z-10 bg-card px-2 py-1.5 font-medium">
															{exercise.exercise_name}
														</td>
														{columns.map((score) => {
															const value = byScore.get(score);
															const isReached = reached === score;
															return (
																<td
																	key={score}
																	className={`px-1 py-1.5 text-center tabular-nums ${
																		isReached
																			? "bg-volt/15 font-semibold text-foreground"
																			: "text-muted-foreground"
																	}`}
																	aria-current={isReached ? "true" : undefined}
																>
																	{value === undefined ? "–" : formatValueByUnit(value, exercise.unit)}
																</td>
															);
														})}
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							</section>
						)}

						{description && (
							<section className="space-y-1.5">
								<p className="label-tactical">{t("lgSourcesTitle")}</p>
								<p className="text-muted-foreground leading-relaxed whitespace-pre-line">
									{description}
								</p>
							</section>
						)}
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
}
